// Lot 536 — Lissage visage de la caméra intégrée (MediaPipe Face Landmarker, 478 points) : la peau du visage est
// adoucie (flou gaussien masqué, bords fondus) sans toucher les yeux, les sourcils ni la bouche. Moteur + modèle servis
// par le backend (/api/mediapipe/v1, cache immuable) : rien dans le bundle web ni dans l'APK, chargés à la demande.
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/mediapipe/v1`;

// Off · Léger · Moyen · Fort — alpha = part de l'image lissée, blur = rayon en fraction de la largeur du visage.
export const BEAUTY_LEVELS = [
  { level: 0, key: "off" },
  { level: 1, key: "light", alpha: 0.45, blur: 0.016 },
  { level: 2, key: "medium", alpha: 0.65, blur: 0.022 },
  { level: 3, key: "strong", alpha: 0.85, blur: 0.03 },
];
export const BEAUTY_STORAGE_KEY = "yalola.camera_beauty";
export const readBeautyLevel = () => { try { const n = Number(localStorage.getItem(BEAUTY_STORAGE_KEY)); return n >= 0 && n <= 3 ? n : 0; } catch { return 0; } };
export const storeBeautyLevel = (n) => { try { localStorage.setItem(BEAUTY_STORAGE_KEY, String(n)); } catch { /* noop */ } };

// Boucles de points MediaPipe (FACEMESH_FACE_OVAL / LEFT_EYE / RIGHT_EYE / LIPS extérieures / sourcils).
const FACE_OVAL = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109];
const HOLES = [
  [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466],
  [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246],
  [61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185],
  [276, 283, 282, 295, 285, 336, 296, 334, 293, 300],
  [46, 53, 52, 65, 55, 107, 66, 105, 63, 70],
];

let landmarkerPromise = null;
let runningMode = "VIDEO";
let delegate = "GPU";
const MODEL = `${BASE}/face_landmarker.task`;
/** Charge (une fois) le moteur WASM + le modèle (GPU si possible, sinon CPU) et le place dans le mode demandé
 *  (VIDEO = caméra, IMAGE = photo de l'éditeur). Rejette si indisponible. */
export const loadFaceLandmarker = async (mode = "VIDEO") => {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      const { FilesetResolver, FaceLandmarker } = await import("@mediapipe/tasks-vision");
      const files = await FilesetResolver.forVisionTasks(`${BASE}/wasm`);
      const make = async (d) => {
        const lm = await FaceLandmarker.createFromOptions(files, {
          baseOptions: { modelAssetPath: MODEL, delegate: d },
          runningMode: "VIDEO", numFaces: 2, outputFaceBlendshapes: false, outputFacialTransformationMatrixes: false,
        });
        delegate = d;
        return lm;
      };
      runningMode = "VIDEO";
      // Délégué : GPU (WebGL) d'abord — sauf si un appareil le rend contre-productif (WebGL logiciel) : la caméra bascule
      // en CPU via switchDelegate() quand la détection dépasse ~90 ms ; `localStorage['yalola.beauty_delegate'] = 'CPU'`
      // force le CPU d'emblée (dépannage / QA).
      let forced = null;
      try { forced = localStorage.getItem("yalola.beauty_delegate"); } catch { /* noop */ }
      if (forced === "CPU") return make("CPU");
      try { return await make("GPU"); } catch { return make("CPU"); }
    })().catch((e) => { landmarkerPromise = null; throw e; });
  }
  const lm = await landmarkerPromise;
  if (runningMode !== mode) { await lm.setOptions({ runningMode: mode }); runningMode = mode; }
  return lm;
};
export const currentDelegate = () => delegate;
/** Reconstruit le graphe avec l'autre délégué (modèle relu depuis le cache HTTP). */
export const switchDelegate = async (d) => {
  const lm = await landmarkerPromise;
  if (!lm || delegate === d) return;
  await lm.setOptions({ baseOptions: { modelAssetPath: MODEL, delegate: d } });
  delegate = d;
};

/** Lisse les visages d'un canvas déjà dessiné (photo : éditeur Filtres, cuisson). Retourne le nombre de visages. */
export const smoothCanvasFaces = async (ctx, w, h, level) => {
  if (!BEAUTY_LEVELS[level]?.alpha) return 0;
  const lm = await loadFaceLandmarker("IMAGE");
  const faces = lm.detect(ctx.canvas).faceLandmarks || [];
  drawSmoothFaces(ctx, faces, w, h, level);
  return faces.length;
};

/** Aperçu lissé d'une image (URL blob ou distante) → { url (blob), faces }. Réduit à `maxW` pour rester rapide. */
export const smoothImageToUrl = async (src, level, maxW = 1600) => {
  const img = await new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = rej; im.src = src; });
  const k = Math.min(1, maxW / img.naturalWidth);
  const c = document.createElement("canvas"); c.width = Math.max(1, Math.round(img.naturalWidth * k)); c.height = Math.max(1, Math.round(img.naturalHeight * k));
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, c.width, c.height);
  const faces = await smoothCanvasFaces(ctx, c.width, c.height, level);
  const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.92));
  return { url: URL.createObjectURL(blob), faces };
};

const loopPath = (ctx, pts, idx, w, h) => {
  ctx.beginPath();
  idx.forEach((i, k) => { const p = pts[i]; if (!p) return; if (k === 0) ctx.moveTo(p.x * w, p.y * h); else ctx.lineTo(p.x * w, p.y * h); });
  ctx.closePath();
};
const faceWidth = (pts, w) => { let lo = 1, hi = 0; FACE_OVAL.forEach((i) => { const p = pts[i]; if (p) { lo = Math.min(lo, p.x); hi = Math.max(hi, p.x); } }); return Math.max(1, (hi - lo) * w); };

export const makeScratch = () => ({ blur: document.createElement("canvas"), mask: document.createElement("canvas"), tmp: document.createElement("canvas") });

// Safari < 18 n'a pas CanvasRenderingContext2D.filter → repli : flou par réduction / agrandissement (lissage bilinéaire).
const supportsFilter = typeof document !== "undefined" && typeof document.createElement("canvas").getContext("2d")?.filter === "string";
const blurInto = (dst, src, w, h, radius, tmp) => {
  if (supportsFilter) { dst.filter = `blur(${radius}px)`; dst.drawImage(src, 0, 0, w, h); dst.filter = "none"; return; }
  const f = Math.max(1, radius / 1.5);
  const tw = Math.max(1, Math.round(w / f)), th = Math.max(1, Math.round(h / f));
  if (tmp.width !== tw || tmp.height !== th) { tmp.width = tw; tmp.height = th; }
  const tctx = tmp.getContext("2d");
  tctx.imageSmoothingEnabled = true; tctx.imageSmoothingQuality = "high";
  tctx.clearRect(0, 0, tw, th); tctx.drawImage(src, 0, 0, w, h, 0, 0, tw, th);
  dst.imageSmoothingEnabled = true; dst.imageSmoothingQuality = "high";
  dst.drawImage(tmp, 0, 0, tw, th, 0, 0, w, h);
};

/** Lisse les visages dans `ctx` (le canvas contient déjà l'image, taille w×h). `faces` = faceLandmarks normalisés. */
export const drawSmoothFaces = (ctx, faces, w, h, level, scratch = makeScratch()) => {
  const cfg = BEAUTY_LEVELS[level];
  if (!cfg?.alpha || !faces || faces.length === 0) return false;
  const { blur: blurC, mask: maskC, tmp } = scratch;
  if (blurC.width !== w || blurC.height !== h) { blurC.width = w; blurC.height = h; maskC.width = w; maskC.height = h; }
  const widest = Math.max(...faces.map((pts) => faceWidth(pts, w)));
  const radius = Math.max(1.5, widest * cfg.blur);
  const feather = Math.max(2, widest * 0.04);

  const m = maskC.getContext("2d");
  m.setTransform(1, 0, 0, 1, 0, 0); m.filter = "none"; m.globalCompositeOperation = "source-over";
  m.clearRect(0, 0, w, h);
  m.fillStyle = "#fff";
  faces.forEach((pts) => { loopPath(m, pts, FACE_OVAL, w, h); m.fill(); });
  m.globalCompositeOperation = "destination-out";
  m.lineJoin = "round";
  faces.forEach((pts) => {
    const fw = faceWidth(pts, w);
    m.lineWidth = fw * 0.035;
    HOLES.forEach((loop) => { loopPath(m, pts, loop, w, h); m.fill(); m.stroke(); });
  });

  const b = blurC.getContext("2d");
  b.setTransform(1, 0, 0, 1, 0, 0); b.globalCompositeOperation = "source-over"; b.globalAlpha = 1;
  b.clearRect(0, 0, w, h);
  blurInto(b, ctx.canvas, w, h, radius, tmp);
  b.globalCompositeOperation = "destination-in";
  blurInto(b, maskC, w, h, feather, tmp);

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = cfg.alpha;
  ctx.drawImage(blurC, 0, 0);
  ctx.restore();
  return true;
};
